import { getAccessToken } from './authHelper';

const API_BASE_URL = "https://4rvom48k45.execute-api.us-east-1.amazonaws.com/dev";

// Helper function to create headers with authorization
const createAuthHeaders = async () => {
  const accessToken = await getAccessToken();
  return {
    "Content-Type": "application/json",
    "Authorization": accessToken
  };
};

// Helper function to handle API responses
const handleApiResponse = async (response) => {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error: ${response.status} - ${errorText}`);
  }
  
  // Parse JSON response
  try {
    const responseData = await response.json();
    
    // Handle backend response structure: { statusCode, headers, body }
    if (responseData && responseData.body) {
      // Body is already an object, return it directly
      return responseData.body;
    }
    
    // If no body field, return the entire response
    return responseData;
  } catch (error) {
    console.error("Failed to parse response as JSON:", error);
    throw new Error("Invalid JSON response from API");
  }
};

// Public data service (proxy-backed lookups)
export const publicDataApiService = {
  // Lookup company data by registration number (ח"פ)
  async getCompanyByHp(companyId) {
    try {
      const headers = await createAuthHeaders();
      const params = new URLSearchParams({
        resource_id: 'f004176c-b85f-4542-8901-7b3176f9a054',
        filters: JSON.stringify({ "מספר חברה": companyId })
      });
      const url = `${API_BASE_URL}/get_hp_data?${params.toString()}`;
      const response = await fetch(url, {
        method: "GET",
        headers
      });
      const result = await handleApiResponse(response);
      return result;
    } catch (error) {
      console.error("Failed to get company data:", error);
      throw error;
    }
  }
};

// API Service for transactions
export const transactionApiService = {
  // Get all transactions from API
  async getAllTransactions() {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/previous_requests?action=getall`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      });
      
      const result = await handleApiResponse(response);
      
      // Handle new response structure: { success, message, data }
      let data;
      if (result && result.success === true && result.data) {
        data = result.data;
      } else if (result && result.success === false) {
        throw new Error(result.message || "Failed to fetch transactions");
      } else {
        // Fallback: assume it's the old format
        data = result;
      }
      
      // Ensure data is an array
      const dataArray = Array.isArray(data) ? data : [];
      
      // Transform API data to match local format
      const transformedData = dataArray.map(item => ({
        key: item.key,
        bankId: item.bank_id,
        branchId: item.branch_id,
        accountId: item.account_id,
        payeeName: item.payee_name,
        payeeId: item.payee_id,
        payeeNumber: item.payee_number,
        amount: item.amount,
        user_id: item.user_id,
        created_at: item.created_at // preserve created_at for sorting
      }));
      
      return transformedData;
    } catch (error) {
      console.error("Failed to fetch transactions from API:", error);
      throw error;
    }
  },

  // Insert new transaction
  async insertTransaction(transaction) {
    try {
      const headers = await createAuthHeaders();
      const apiPayload = {
        bank_id: transaction.bankId,
        branch_id: transaction.branchId,
        account_id: transaction.accountId,
        payee_name: transaction.payeeName,
        payee_id: transaction.payeeId,
        payee_number: transaction.payeeNumber,
        amount: transaction.amount,
        key: transaction.key
      };

      const response = await fetch(`${API_BASE_URL}/previous_requests?action=insert`, {
        method: "POST",
        headers,
        body: JSON.stringify(apiPayload),
      });

      const result = await handleApiResponse(response);
      
      // Check if the operation was successful
      if (result && result.success === false) {
        throw new Error(result.message || "Failed to insert transaction");
      }
      
      return transaction;
    } catch (error) {
      console.error("Failed to insert transaction:", error);
      throw error;
    }
  },

  // Update existing transaction
  async updateTransaction(transaction) {
    try {
      const headers = await createAuthHeaders();
      const apiPayload = {
        bank_id: transaction.bankId,
        branch_id: transaction.branchId,
        account_id: transaction.accountId,
        payee_name: transaction.payeeName,
        payee_id: transaction.payeeId,
        payee_number: transaction.payeeNumber,
        amount: transaction.amount,
        key: transaction.key
      };

      const response = await fetch(`${API_BASE_URL}/previous_requests?action=update`, {
        method: "POST",
        headers,
        body: JSON.stringify(apiPayload),
      });

      const result = await handleApiResponse(response);
      
      // Check if the operation was successful
      if (result && result.success === false) {
        throw new Error(result.message || "Failed to update transaction");
      }
      
      return transaction;
    } catch (error) {
      console.error("Failed to update transaction:", error);
      throw error;
    }
  },

  // Delete specific transaction
  async deleteTransaction(key) {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/previous_requests?action=delete`, {
        method: "POST",
        headers,
        body: JSON.stringify({ key }),
      });

      const result = await handleApiResponse(response);
      
      // Check if the operation was successful
      if (result && result.success === false) {
        throw new Error(result.message || "Failed to delete transaction");
      }
      
      return key;
    } catch (error) {
      console.error("Failed to delete transaction:", error);
      throw error;
    }
  },

  // Delete all transactions
  async deleteAllTransactions() {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/previous_requests?action=deleteall`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      });

      const result = await handleApiResponse(response);
      
      // Check if the operation was successful
      if (result && result.success === false) {
        throw new Error(result.message || "Failed to delete all transactions");
      }
      
      return true;
    } catch (error) {
      console.error("Failed to delete all transactions:", error);
      throw error;
    }
  },

  // Sync local storage with API
  async syncWithApi() {
    try {
      return await this.getAllTransactions();
    } catch (error) {
      console.error("Failed to sync with API:", error);
      throw error;
    }
  }
};

// API Service for favorites
export const favoritesApiService = {
  async addOrIncrementFavorite(favoriteData) {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/favorites?action=addOrIncrement`, {
        method: "POST",
        headers,
        body: JSON.stringify(favoriteData),
      });

      const result = await handleApiResponse(response);
      
      // Check if the operation was successful
      if (result && result.success === false) {
        throw new Error(result.message || "Failed to add/increment favorite");
      }
      
      return result;
    } catch (error) {
      console.error("Failed to add/increment favorite:", error);
      throw error;
    }
  },

  async getUserFavorites() {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/favorites?action=getUserDetails`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      });

      const result = await handleApiResponse(response);
      
      // Check if the operation was successful and return the data
      if (result && result.success === true && result.data) {
        return result.data;
      } else if (result && result.success === false) {
        console.warn("API returned error:", result.message);
        return [];
      }
      
      // Fallback: if no success field, assume it's the old format
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error("Failed to fetch favorites:", error);
      return [];
    }
  }
}; 

// API Service for institutions
export const institutionApiService = {
  // Save institution data
  async saveInstitution(institutionData) {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/institutions?action=save`, {
        method: "POST",
        headers,
        body: JSON.stringify(institutionData),
      });

      const result = await handleApiResponse(response);
      
      // Check if the operation was successful
      if (result && result.success === false) {
        throw new Error(result.message || "Failed to save institution data");
      }
      
      return result;
    } catch (error) {
      console.error("Failed to save institution data:", error);
      throw error;
    }
  },

  // Get institution data by user ID
  async getInstitutionByUserId() {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/institutions?action=get`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      });

      const result = await handleApiResponse(response);
      
      // Check if the operation was successful and return the data
      if (result && result.success === true && result.data) {
        return result.data;
      } else if (result && result.success === false) {
        console.warn("API returned error:", result.message);
        return null;
      }
      
      // Fallback: if no success field, assume it's the old format
      return result;
    } catch (error) {
      console.error("Failed to fetch institution data:", error);
      return null;
    }
  },

  // Accept terms agreement
  async acceptTerms() {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/institutions?action=acceptterms`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      });

      const result = await handleApiResponse(response);
      
      // Check if the operation was successful
      if (result && result.success === false) {
        throw new Error(result.message || "Failed to accept terms");
      }
      
      return result;
    } catch (error) {
      console.error("Failed to accept terms:", error);
      throw error;
    }
  },

};

// API Service for Masav file generation
export const masavFileApiService = {
  // Check if user can download (returns boolean) - token is sent in headers via createAuthHeaders
  // If transactions are provided, Lambda will update the download count
  async generateMasavFile(transactions = null) {
    try {
      const headers = await createAuthHeaders();
      const body = transactions ? { transactions } : {};
      const response = await fetch(`${API_BASE_URL}/generate_masav_file`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      // Check if response is ok
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      // Parse response - Lambda returns object with limitReached or remainingDownloads
      const result = await handleApiResponse(response);
      
      // Handle response structure: { success, data } or { body: { success, data } }
      const bodyData = result.body || result;
      
      if (bodyData && bodyData.success === false) {
        return false;
      }

      // Get the data object from response
      const data = bodyData?.data || bodyData;
      
      // Check if limitReached is true or remainingDownloads is 0
      if (data && typeof data === 'object') {
        // If limitReached is explicitly true, cannot download
        if (data.limitReached === true) {
          return false;
        }
        // If remainingDownloads is 0 or less, cannot download
        if (typeof data.remainingDownloads === 'number' && data.remainingDownloads <= 0) {
          return false;
        }
        // If we have limitReached: false or remainingDownloads > 0, can download
        if (data.limitReached === false || (typeof data.remainingDownloads === 'number' && data.remainingDownloads > 0)) {
          return true;
        }
      }
      
      // Fallback: if data is a boolean, return it
      if (typeof data === 'boolean') {
        return data;
      }
      
      // Default: if we can't determine, return false to be safe
      return false;
    } catch (error) {
      console.error("Failed to check download availability:", error);
      // On error, return false to be safe
      return false;
    }
  },

  async getUsageInfo() {
    const headers = await createAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/generate_masav_file`, {
      method: "POST",
      headers,
      body: JSON.stringify({}),
    });

    // Get response text to see what Lambda is returning
    const responseText = await response.text();

    // Check if response is ok
    if (!response.ok) {
      // Don't hard-fail the app if usage info endpoint is broken.
      // (Common when backend schema changes, e.g. removed columns like `email`.)
      let errorBody = null;
      try {
        const errorData = JSON.parse(responseText);
        errorBody = errorData?.body || errorData;
      } catch {
        // ignore
      }

      console.warn("getUsageInfo failed:", {
        status: response.status,
        message: errorBody?.message,
        error: errorBody?.error,
      });

      return null;
    }

    // Response is OK - parse it
    try {
      const result = JSON.parse(responseText);
      
      const bodyData = result.body || result;
      
      if (bodyData && bodyData.success === false) {
        console.warn("getUsageInfo returned success=false:", bodyData.message);
        return null;
      }

      const data = bodyData?.data || bodyData;

      return {
        accountType: (data?.accountType || data?.account_type || "").toLowerCase(),
        downloadCount: data?.downloadCount ?? data?.download_count ?? null,
        monthlyLimit: data?.monthlyLimit ?? data?.monthly_limit ?? null,
        remainingDownloads: data?.remainingDownloads ?? data?.remaining_downloads ?? null,
      };
    } catch (parseError) {
      console.warn("getUsageInfo - Failed to parse response:", parseError);
      return null;
    }
  }
};

// API Service for saved transfer tables
export const savedTablesApiService = {
  // Get all saved tables for the current user (user identified by token in Authorization header)
  async getSavedTables() {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/saved_tables`, {
        method: "GET",
        headers,
      });

      // If Lambda doesn't exist yet, return empty array
      if (!response.ok && response.status === 404) {
        console.warn("Saved tables endpoint not found, returning empty array");
        return [];
      }

      const result = await handleApiResponse(response);
      
      // Handle response structure: { success, message, data }
      let data;
      if (result && result.success === true && result.data) {
        data = result.data;
      } else if (result && result.success === false) {
        console.warn("API returned error:", result.message);
        return [];
      } else {
        // Fallback: assume it's the old format
        data = result;
      }
      
      // Ensure data is an array
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Failed to fetch saved tables:", error);
      // Don't throw - return empty array if Lambda doesn't exist
      return [];
    }
  },

  // Save a transfer table (user identified by token in Authorization header)
  // Sends all current transactions in the table
  async saveTable(tableName, institution, allTransactions) {
    try {
      const headers = await createAuthHeaders();
      const payload = {
        table_name: tableName,
        institution: {
          institution_id: institution.institutionId,
          institution_name: institution.institutionName,
          sending_institution_id: institution.sendingInstitutionId,
          serial_number: institution.serialNumber
        },
        transactions: allTransactions.map(t => ({
          bank_id: t.bankId,
          branch_id: t.branchId,
          account_id: t.accountId,
          payee_name: t.payeeName,
          payee_id: t.payeeId,
          payee_number: t.payeeNumber,
          amount: t.amount,
          key: t.key
        }))
      };

      const response = await fetch(`${API_BASE_URL}/saved_tables`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      // If Lambda doesn't exist yet, just log and return success
      if (!response.ok && response.status === 404) {
        console.warn("Saved tables endpoint not found, skipping save");
        return { success: true, message: "Lambda not configured yet" };
      }

      const result = await handleApiResponse(response);
      
      // Check if the operation was successful
      if (result && result.success === false) {
        throw new Error(result.message || "Failed to save table");
      }
      
      return result;
    } catch (error) {
      console.error("Failed to save table:", error);
      // Don't throw - just log the error so download can continue
      console.warn("Table save failed, but download will continue:", error.message);
      return { success: false, message: error.message };
    }
  }
}; 